import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import CreatableSelect from "react-select/creatable";
import {
    getReactSelectTheme,
    getReactSelectStyling,
} from "../../utils/ReactSelectStyles";
import CheckBox from "../../components/CheckBox/CheckBox";
import LookupsAPI from "../../API/lookups.api";
import ValidationError from "../../components/Shared/ValidationError";
import { validateEmail } from "../../utils/Helpers";

const customReactSelectStyles = {
    multiValue: (base, { data }) => {
        return {
            ...base,

            border: "1px solid #ced4da",
            borderLeft: `5px solid ${
                data.isCreated ? "hsl(24deg 100% 50%)" : "hsl(227deg 30% 34%)"
            }`,
            padding: 0.5,
            borderRaduis: "1rem",
            backgroundColor: "#e9eff6",
        };
    },
};

const NotificationConfiguration = ({
    data,
    setData,
    alertRecipients,
    setAlertRecipients,
    isFormSubmitted,
    error,
    setError,
}) => {
    //General states
    const { t } = useTranslation();

    //Data states
    const [alertRecipientsLookup, setAlertRecipientsLookup] = useState([]);

    //Use effects
    useEffect(() => {
        getUsers();
    }, []);

    //Data fetching
    const getUsers = async () => {
        const result = await LookupsAPI.getSupervisedUsers(true);
        if (result) {
            const uniqueUsers = [];
            result.forEach((elem) => {
                if (
                    uniqueUsers.findIndex((object) => {
                        return object.value === elem.id;
                    }) === -1
                ) {
                    uniqueUsers.push({ value: elem.id, label: elem.email });
                }
            });

            setAlertRecipientsLookup(uniqueUsers);
        }
    };

    //Handlers
    const reformatRecipients = (value) => {
        setAlertRecipients(value);
    };

    const handleCheckBoxChange = (e) => {
        setData((prevData) => ({
            ...prevData,
            notificationConfigurations: {
                ...prevData.notificationConfigurations,
                [e.target.name]: e.target.checked,
            },
        }));
    };

    const createRecipients = (input) => {
        const validEmail = validateEmail(input);
        if (validEmail) {
            const option = {
                label: input,
                value: input,
                isCreated: true,
            };

            setAlertRecipients((prevData) => [...prevData, option]);
        } else {
            setError((prevData) => ({
                ...prevData,
                email: "Email is not valid",
            }));
        }
    };

    //Renderers
    const renderNotificationConfigurationFields = () => {
        return (
            <div>
                <div className="input-group w-full">
                    <div className="tags px-2 py-2 whitespace-wrap">
                        <h4
                            className={`uppercase text-xs required ${
                                isFormSubmitted && error["notificationTypes"]
                                    ? "m-0"
                                    : ""
                            }`}
                        >
                            <Trans i18nKey="notificationSettings">
                                Select type of notitifaction to be sent
                            </Trans>
                        </h4>
                        {isFormSubmitted && (
                            <div className="mb-2">
                                <ValidationError
                                    customClassNames="m-0"
                                    errorMessage={error["notificationTypes"]}
                                />
                            </div>
                        )}
                        <CheckBox
                            name="emailNotification"
                            checked={
                                data.notificationConfigurations
                                    ?.emailNotification || false
                            }
                            label={t("emailNotification")}
                            onChangeHandler={handleCheckBoxChange}
                            containerClassName="ml-3"
                            checkBoxClassName="top-2"
                        />
                        <CheckBox
                            name="systemNotification"
                            checked={
                                data.notificationConfigurations
                                    ?.systemNotification || false
                            }
                            label={t("systemNotification")}
                            onChangeHandler={handleCheckBoxChange}
                            containerClassName="ml-3"
                            checkBoxClassName="top-2"
                        />
                    </div>
                </div>

                <div className="input-group w-full">
                    <div className="tags px-2 py-2 whitespace-wrap">
                        <h4 className="uppercase text-xs required">
                            <Trans i18nKey="alertRecipients">
                                Alert Recipients
                            </Trans>
                        </h4>

                        <CreatableSelect
                            isMulti
                            isClearable
                            isSearchable
                            options={alertRecipientsLookup}
                            value={alertRecipients || ""}
                            onChange={reformatRecipients}
                            onCreateOption={createRecipients}
                            placeholder={t("selectPlaceholder", {
                                entity: t("alertRecipients"),
                            })}
                            isDisabled={
                                !data.notificationConfigurations
                                    ?.emailNotification &&
                                !data.notificationConfigurations
                                    ?.systemNotification
                            }
                            // isLoading={isSelectLoading}
                            styles={getReactSelectStyling({
                                additionalStyles: customReactSelectStyles,
                                isMultiSelect: true,
                                valueContainerStyle: false,
                            })}
                            theme={getReactSelectTheme}
                        />
                        {isFormSubmitted && (
                            <ValidationError
                                errorMessage={error["recipients"]}
                            />
                        )}
                        <ValidationError errorMessage={error["email"]} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <h3 className="text-primary-color-blue font-medium capitalize mt-3">
                <Trans i18nKey="notificationConfigurations">
                    Notification Configurations
                </Trans>
            </h3>
            <hr className="mt-2 mb-5" />
            {renderNotificationConfigurationFields()}
        </>
    );
};

export default NotificationConfiguration;
